import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { LucideSparkles, LucideX } from '@lucide/angular';
import { DEMO_TOUR_ID } from '../demo/demo-mode';
import { findAnchor, sameSpot, Spot, spotOf } from '../tour-anchor';
import { performStep } from '../tour-action';
import { TourStep } from '../tour.model';
import { Corner, cornerAvoiding } from '../tour-placement';
import { TourService } from '../tour.service';

/** Marge autour de l'élément mis en surbrillance, en pixels. */
const SPOT_MARGIN = 4;

/**
 * La zone de texte d'un parcours, et la surbrillance de l'élément de l'étape. Ni l'une ni l'autre ne
 * bloque l'écran : « Suivant » fait le geste de l'étape, mais l'utilisateur peut le faire lui-même,
 * et la surbrillance se pose dès que l'élément apparaît.
 */
@Component({
  selector: 'app-tour-panel',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatButtonModule, MatTooltip, LucideSparkles, LucideX],
  templateUrl: './tour-panel.html',
  styleUrls: ['./tour-panel.scss'],
})
export class TourPanel {
  protected readonly tours = inject(TourService);
  protected readonly spot = signal<Spot | null>(null, { equal: sameSpot });
  protected readonly margin = SPOT_MARGIN;
  /**
   * Ce que « Suivant » n'a pas pu faire à l'étape : l'utilisateur le fait lui-même, et le clic
   * suivant avance sans refaire les gestes.
   */
  protected readonly failure = signal<string | null>(null);
  private performing = false;
  /** Le coin du panneau, qui s'écarte de l'élément en surbrillance pour ne pas le cacher. */
  protected readonly corner = signal<Corner>('bottom-right');
  private readonly layer = viewChild<ElementRef<HTMLElement>>('layer');
  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');

  constructor() {
    // Un onglet de démonstration joue son parcours dès l'ouverture, et le reprend au rechargement.
    const demoTourId = inject(DEMO_TOUR_ID, { optional: true });
    if (demoTourId) this.tours.start(demoTourId);

    effect((onCleanup) => {
      const layer = this.layer()?.nativeElement;
      if (layer && this.tours.tour()) onCleanup(keepOnTop(layer));
    });

    effect((onCleanup) => {
      const step = this.tours.step();
      this.failure.set(null);
      if (!step) {
        this.spot.set(null);
        return;
      }
      onCleanup(this.track(step.anchor));
    });
  }

  /**
   * Fait les gestes de l'étape, puis passe à la suivante. Un geste qui ne se fait pas arrête
   * l'étape, et le panneau dit lequel ; après quoi « Suivant » avance seulement.
   */
  protected async next(): Promise<void> {
    const step = this.tours.step();
    if (this.performing) return;
    if (step && !this.failure()) {
      this.performing = true;
      const failed = await performStep(step).finally(() => (this.performing = false));
      if (failed !== null) {
        this.failure.set(failureMessage(step, failed));
        return;
      }
    }
    this.tours.next();
  }

  /** Suit l'ancre de l'étape : elle apparaît, disparaît ou bouge avec les dialogues et le défilement. */
  private track(anchor: string): () => void {
    let shown: HTMLElement | null = null;
    let frame = 0;
    const locate = () => {
      frame = 0;
      const element = findAnchor(anchor);
      if (element && element !== shown) {
        element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
      shown = element;
      const spot = element ? spotOf(element) : null;
      this.spot.set(spot);
      const panel = this.panel()?.nativeElement;
      if (panel) {
        this.corner.set(
          cornerAvoiding(
            spot,
            { width: panel.offsetWidth, height: panel.offsetHeight },
            { width: window.innerWidth, height: window.innerHeight },
          ),
        );
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(locate);
    };
    locate();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      if (frame) cancelAnimationFrame(frame);
    };
  }
}

function failureMessage(step: TourStep, index: number): string {
  const actions = step.actions ?? [];
  const kind = actions[index].kind === 'click' ? 'un clic' : 'une saisie';
  const gesture =
    actions.length > 1 ? `le geste ${index + 1} sur ${actions.length}, ${kind}` : kind;
  return `« Suivant » n’a pas pu faire ${gesture} : faites-le vous-même, puis cliquez « Suivant ».`;
}

/**
 * Les dialogues et les menus de la CDK s'ouvrent dans la couche supérieure du navigateur
 * (`popover`), au-dessus de tout `z-index`, avec un fond qui prendrait les clics destinés au
 * panneau. Le parcours s'y place aussi, et repasse devant chaque fenêtre qui s'ouvre après lui :
 * dans cette couche, la dernière ouverte passe devant.
 */
function keepOnTop(layer: HTMLElement): () => void {
  const raise = () => {
    if (layer.matches(':popover-open')) layer.hidePopover();
    layer.showPopover();
  };
  const opensPopover = (node: Node) =>
    node instanceof HTMLElement &&
    node !== layer &&
    (node.hasAttribute('popover') || !!node.querySelector('[popover]'));
  raise();
  const observer = new MutationObserver((records) => {
    if (records.some((record) => Array.from(record.addedNodes).some(opensPopover))) raise();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  return () => {
    observer.disconnect();
    if (layer.matches(':popover-open')) layer.hidePopover();
  };
}
